/**
 * ErrorState — explanatory error panel shown when a data fetch fails (as
 * opposed to EmptyFilterState, which is for zero results from a *successful*
 * fetch). Distinguishes "not found" (404) from a transient server/network
 * failure so the copy and the retry affordance match the situation.
 *
 * Pages opt-in by rendering this when their query reports `isError`, passing
 * the error and (when retryable) an `onRetry` wired to the query's `refetch`.
 */
import { AlertTriangle, RotateCw } from 'lucide-react';
import { useT } from '../../i18n';
import { ApiError } from '../../api/client';

interface ErrorStateProps {
  /** The thrown error (ApiError carries an HTTP status; anything else is treated as transient). */
  error?: unknown;
  /** Wire to the query's `refetch`. Omit to hide the retry button (e.g. for 4xx that won't change). */
  onRetry?: () => void;
  /** Compact variant for inline placement inside a chart card. */
  compact?: boolean;
}

export function ErrorState({ error, onRetry, compact = false }: ErrorStateProps) {
  const t = useT();
  const status = error instanceof ApiError ? error.status : undefined;
  // 4xx (except request-timeout) won't resolve on retry — hide the button.
  const retryable = onRetry && (status === undefined || status === 0 || status === 408 || status >= 500);

  return (
    <div
      role="alert"
      className={`flex flex-col items-center gap-[12px] text-center ${
        compact ? 'py-[24px] px-[16px]' : 'py-[48px] px-[24px]'
      }`}
    >
      <div className="w-[40px] h-[40px] rounded-full bg-[var(--surface-2)] flex items-center justify-center">
        <AlertTriangle size={18} className="text-[var(--danger,#dc2626)]" />
      </div>

      <div>
        <h3 className="text-[15px] font-semibold text-[var(--text-1)] mb-[4px]">
          {t('common.error.title')}
        </h3>
        <p className="text-[12px] text-[var(--text-3)] max-w-[400px]">
          {t('common.error.body')}
        </p>
      </div>

      {retryable && (
        <button
          onClick={onRetry}
          className="
            flex items-center gap-[6px] px-[12px] py-[6px] rounded-[var(--r2)]
            border border-[var(--border)] text-[12px] font-medium
            text-[var(--text-2)] hover:text-[var(--text-1)] hover:border-[var(--accent)]
            transition-colors
          "
        >
          <RotateCw size={13} /> {t('common.retry')}
        </button>
      )}
    </div>
  );
}
